import React, { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, X } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { ScreenHead, Section, ListCard, Empty, Skeletons, Avatar, GapList, titleCase, useClients } from "./psPrimitives";
import { usePsClient, useClientProviders } from "./psMasters";

const NEVER_RE = /^\s*(never|don'?t|do not|no |avoid|exclude|stop)/i;

export function CodingRulesScreen({ mode, onOpenDq }) {
  const { data: providers = [], isLoading } = useClientProviders();
  const { cid: curCid } = usePsClient();
  const { data: allClients = [] } = useClients();
  const clients = allClients.filter((c) => String(c.client_id) === String(curCid));
  const [sub, setSub] = useState(null); // { kind: "provider"|"client", id }

  if (isLoading) return <><ScreenHead eyebrow="Billing Rules" title="Coding rules" /><Skeletons /></>;

  const nRules = (x) => (x.inclusion_rules || []).length + (x.exclusion_rules || []).length;
  const provCid = (p) => String(p.client_id || p.client_4digit_id || "");
  if (sub?.kind === "client") {
    const c = clients.find((x) => String(x.client_id) === sub.id);
    if (c) return <CodingProviderPage key={`c${c.client_id}`} client={c} onBack={() => setSub(null)} />;
  }
  const current = sub?.kind === "provider" ? providers.find((p) => p.npi_number === sub.id) : null;
  if (current) {
    const c = clients.find((x) => String(x.client_id) === provCid(current));
    return <CodingProviderPage key={current.npi_number} provider={current} parent={c} onBack={() => setSub(null)} onOpenClient={c ? () => setSub({ kind: "client", id: String(c.client_id) }) : null} />;
  }

  return (
    <div className="fade-up">
      <ScreenHead eyebrow="Billing Rules · optional" title="Coding rules"
        lede="Plain-English rules for revQ's coder. Client rules apply to every provider in the client; provider rules add to them." />
      {clients.length ? (
        <Section title="Client rules" testid="ps-coding-clients">
          <ListCard>
            {clients.map((c, i) => {
              const n = nRules(c), np = providers.filter((p) => provCid(p) === String(c.client_id)).length;
              return (
                <button key={c.client_id} onClick={() => setSub({ kind: "client", id: String(c.client_id) })} data-testid={`ps-coding-client-${c.client_id}`}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3.5 hover:bg-black/[.03] transition ${i ? "border-t border-[#E9E9EC]" : ""}`}>
                  <div className="w-[38px] h-[38px] rounded-full flex items-center justify-center text-[12.5px] font-bold flex-shrink-0 bg-[#F1F1F4] text-[var(--gray2)]">{String(c.client_id).slice(0, 2)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[var(--ink)] truncate">{titleCase(c.description || c.name || "Client")} <span className="font-mono text-[var(--gray2)]">{c.client_id}</span></div>
                    <div className="text-[10.5px] text-[var(--gray2)] mt-0.5">Every provider in this client · {np} provider{np === 1 ? "" : "s"}</div>
                  </div>
                  <span className="text-[12px] text-[var(--gray2)]">{n ? `${n} rule${n === 1 ? "" : "s"}` : "Not set"}</span>
                  <ChevronRight size={16} className="text-[var(--gray3)]" />
                </button>
              );
            })}
          </ListCard>
        </Section>
      ) : null}
      <Section title="Provider rules" testid="ps-coding-providers">
      {providers.length === 0 ? <Empty>Add a provider first.</Empty> : (
        <ListCard testid="ps-coding-list">
          {providers.map((p, i) => {
            const inc = (p.inclusion_rules || []).length, exc = (p.exclusion_rules || []).length;
            const parts = [];
            if (inc) parts.push(`${inc} include`);
            if (exc) parts.push(`${exc} exclude`);
            return (
              <button key={p.id} onClick={() => setSub({ kind: "provider", id: p.npi_number })} data-testid={`ps-coding-row-${p.npi_number}`}
                className={`w-full text-left flex items-center gap-3 px-4 py-3.5 hover:bg-black/[.03] transition ${i ? "border-t border-[#E9E9EC]" : ""}`}>
                <Avatar text={p.provider_name} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[var(--ink)] truncate">{p.provider_name}</div>
                  <div className="text-[10.5px] text-[var(--gray2)] mt-0.5">{parts.length ? parts.join(" · ") : "Not set"}</div>
                </div>
                <ChevronRight size={16} className="text-[var(--gray3)]" />
              </button>
            );
          })}
        </ListCard>
      )}
      </Section>
      {mode === "full" && (
        <Section title="Full state · needs API" testid="ps-coding-full">
          <GapList onOpen={onOpenDq} items={[
            { title: "Structured rules", desc: "Target CPT / payer / modifier, enabled toggle", dq: 41 },
            { title: "Modifier permissions", desc: "Which modifiers coders may apply", dq: 50 },
          ]} />
        </Section>
      )}
    </div>
  );
}

function CodingProviderPage({ provider, client, parent, onBack, onOpenClient }) {
  const qc = useQueryClient();
  const src = client || provider;
  const [inc, setInc] = useState(src.inclusion_rules || []);
  const [exc, setExc] = useState(src.exclusion_rules || []);
  const [text, setText] = useState("");
  const [kind, setKind] = useState("always"); // always | never
  const [kindTouched, setKindTouched] = useState(false);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const loadedRef = useRef({ inc: src.inclusion_rules || [], exc: src.exclusion_rules || [] });

  const onType = (v) => {
    setText(v);
    if (!kindTouched) setKind(NEVER_RE.test(v) ? "never" : "always");
  };

  // full-replace save with re-GET-and-compare guard
  const persist = async (nextInc, nextExc) => {
    setSaving(true); setErr("");
    try {
      const server = client
        ? ((await api.get("/api/edi/note-to-claim/clients")).data?.clients ?? []).find((c) => String(c.client_id) === String(client.client_id))
        : ((await api.get("/api/edi/note-to-claim/providers")).data?.providers ?? []).find((p) => p.npi_number === provider.npi_number);
      const sInc = server?.inclusion_rules || [], sExc = server?.exclusion_rules || [];
      const changed = JSON.stringify(sInc) !== JSON.stringify(loadedRef.current.inc) || JSON.stringify(sExc) !== JSON.stringify(loadedRef.current.exc);
      if (changed) {
        setInc(sInc); setExc(sExc); loadedRef.current = { inc: sInc, exc: sExc };
        setErr("These rules changed somewhere else — we reloaded the latest. Re-apply your edit.");
        setSaving(false);
        return false;
      }
      await api.patch(client ? `/api/edi/note-to-claim/clients/${client.client_id}/billing-rules` : `/api/edi/note-to-claim/providers/${provider.npi_number}/billing-rules`, { inclusion_rules: nextInc, exclusion_rules: nextExc });
      loadedRef.current = { inc: nextInc, exc: nextExc };
      qc.invalidateQueries({ queryKey: [client ? "clients" : "providers"] });
      setSaving(false);
      return true;
    } catch (e) { setErr(describeError(e)); setSaving(false); return false; }
  };

  const addRule = async () => {
    const t = text.trim();
    if (!t) return;
    const nextInc = kind === "always" ? [...inc, t] : inc;
    const nextExc = kind === "never" ? [...exc, t] : exc;
    setInc(nextInc); setExc(nextExc);
    const ok = await persist(nextInc, nextExc);
    if (ok) { setText(""); setKind("always"); setKindTouched(false); }
  };

  const editRule = async (list, setList, otherList, isInc, idx, val) => {
    const v = val.trim();
    const next = [...list];
    if (!v) next.splice(idx, 1); else next[idx] = v;
    setList(next);
    await persist(isInc ? next : otherList, isInc ? otherList : next);
  };

  const flip = async (fromInc, idx) => {
    const src = fromInc ? inc : exc, dst = fromInc ? exc : inc;
    const val = src[idx];
    const nextSrc = src.filter((_, i) => i !== idx);
    const nextDst = [...dst, val];
    if (fromInc) { setInc(nextSrc); setExc(nextDst); await persist(nextSrc, nextDst); }
    else { setExc(nextSrc); setInc(nextDst); await persist(nextDst, nextSrc); }
  };

  return (
    <div className="fade-up">
      {onBack ? <button onClick={onBack} data-testid="ps-coding-back" className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--gray2)] hover:text-[var(--ink)] mb-3"><ChevronLeft size={14} /> All rules</button> : null}
      {client ? <ScreenHead eyebrow="Billing Rules · Optional" title={titleCase(client.description || client.name || `Client ${client.client_id}`)} lede={`Client ${client.client_id} · every provider linked to this client follows these rules`} />
        : <ScreenHead eyebrow="Billing Rules · Optional" title={`Coding rules — ${titleCase(provider.provider_name)}`} />}
      <p className="text-[12px] text-[var(--gray1)] mb-4 -mt-2">Write rules in plain English.</p>
      {provider ? <InheritedRules parent={parent} onOpenClient={onOpenClient} /> : null}
      {provider ? <div className="text-[13px] font-semibold text-[var(--gray2)] mb-2.5" data-testid="ps-provider-own-head">This provider's rules</div> : null}

      <div className="flex items-stretch mb-5 rounded-[12px] border border-black/[.14] bg-white overflow-hidden focus-within:border-[#5856D6] transition">
        <input value={text} onChange={(e) => onType(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRule(); } }}
          data-testid="ps-rule-input" placeholder="Tell revQ a rule — e.g. Exclude 99211 when billed with a procedure"
          className="flex-1 px-3 py-2.5 text-[13px] outline-none bg-transparent" disabled={saving} />
        <div className="flex items-stretch border-l border-black/[.1]">
          {["always", "never"].map((k) => (
            <button key={k} onClick={() => { setKind(k); setKindTouched(true); }} data-testid={`ps-rule-kind-${k}`}
              className={`text-[11.5px] font-semibold px-3.5 ${kind === k ? "bg-[var(--ink)] text-white" : "bg-white text-[var(--ink2)] hover:bg-black/[.03]"}`}>
              {k === "always" ? "Always" : "Never"}
            </button>
          ))}
        </div>
      </div>
      {err ? <div className="mb-4 text-[11.5px] text-[#B3261E]" data-testid="ps-rule-error">{err}</div> : null}

      <RuleBand label="Always" rules={inc} onEdit={(i, v) => editRule(inc, setInc, exc, true, i, v)} onDelete={(i) => editRule(inc, setInc, exc, true, i, "")} onFlip={(i) => flip(true, i)} testid="ps-band-always" />
      <RuleBand label="Never" rules={exc} onEdit={(i, v) => editRule(exc, setExc, inc, false, i, v)} onDelete={(i) => editRule(exc, setExc, inc, false, i, "")} onFlip={(i) => flip(false, i)} testid="ps-band-never" />
    </div>
  );
}

function RuleBand({ label, rules, onEdit, onDelete, onFlip, testid }) {
  if (!rules.length) return null;
  const other = label === "Always" ? "Never" : "Always";
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="text-[10px] font-bold tracking-[.1em] uppercase mb-2 text-[var(--gray2)]">{label}</div>
      <ListCard>
        {rules.map((r, i) => (
          <div key={i} className={`group flex items-center gap-3 px-4 py-2.5 ${i ? "border-t border-[#E9E9EC]" : ""}`}>
            <span className="text-[10px] font-bold text-[var(--gray3)] w-4">{i + 1}</span>
            <input defaultValue={r} onBlur={(e) => { if (e.target.value.trim() !== r) onEdit(i, e.target.value); }}
              className="flex-1 bg-transparent text-[12.5px] text-[var(--ink)] outline-none" data-testid={`${testid}-rule-${i}`} />
            <button onClick={() => onFlip(i)} title={`Switch to ${other}`}
              className="text-[9.5px] font-semibold px-2 py-0.5 rounded-[6px] bg-black/[.05] text-[var(--gray1)] hover:bg-black/[.09] transition">{label}</button>
            <button onClick={() => onDelete(i)} aria-label="Delete rule"
              className="text-[var(--gray3)] hover:text-[#B3261E] opacity-0 group-hover:opacity-100 focus:opacity-100 transition p-1"><X size={13} /></button>
          </div>
        ))}
      </ListCard>
    </div>
  );
}

function InheritedRules({ parent, onOpenClient }) {
  const rows = parent ? [...(parent.inclusion_rules || []).map((r) => [r, "Always"]), ...(parent.exclusion_rules || []).map((r) => [r, "Never"])] : [];
  return (
    <div className="mb-6" data-testid="ps-inherited-rules">
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[13px] font-semibold text-[var(--gray2)]">From client {parent?.client_id || "—"}</span>
        {onOpenClient ? <button type="button" onClick={onOpenClient} className="text-[12px] font-semibold text-[#4A4AC2] hover:underline" data-testid="ps-edit-client-rules">Edit client rules</button> : null}
      </div>
      {rows.length ? (
        <ListCard>{rows.map(([r, k], i) => (
          <div key={i} className={`flex items-center gap-3 px-4 py-2.5 ${i ? "border-t border-[#E9E9EC]" : ""}`} data-testid={`ps-inherited-rule-${i}`}>
            <span className="text-[10px] font-bold text-[var(--gray3)] w-4">{i + 1}</span>
            <span className="flex-1 text-[12.5px] text-[var(--ink)]">{r}</span>
            <span className="text-[9.5px] font-semibold px-2 py-0.5 rounded-[6px] bg-black/[.05] text-[var(--gray1)]">{k}</span>
          </div>))}</ListCard>
      ) : <div className="text-[12px] text-[var(--gray2)]" data-testid="ps-inherited-empty">No client rules.</div>}
    </div>
  );
}
